// ============================================================================
// AFB-0041 — Management fee + Usables surfacing (2026-07-02).
//
// The management fee lives on facilities.management_fee_pct (numeric(6,4),
// stored as a FRACTION: 0.0300 = 3%). Business rule (Ben, confirmed
// 2026-07-02): default 3%, ON by default for new records, but always VISIBLE,
// EDITABLE (negotiated variances exist, e.g. 1.5%) and REMOVABLE. Off/remove
// writes 0.00; NULL or 0 both mean "no fee".
//
// NOTE (Ben 2026-07-02): the management fee (monthly add to baseline → final
// bill) and the ANNUAL inflationary kicker (anniversary-based escalator,
// date is negotiable/extendable) are DIFFERENT concepts. This column holds
// the management fee; the annual kicker has no percent column or math yet —
// see the mgmt-fee/kicker scoping doc before touching last/next_kicker_date.
//
// Usables: facilities.tcs_provides_usables (boolean) + usables_detail (jsonb).
// Surfacing only — schema already existed.
//
// Write path: DIRECT client UPDATE. Verified live 2026-07-02:
// enforce_facilities_protected_cols() only guards monthly_rate / status /
// account_manager_eid / account_id — management_fee_pct and the usables
// columns are NOT protected, and facilities RLS grants UPDATE to any TCS
// employee (tcs_employee_update_nonprotected). No RPC needed.
//
// Do NOT touch last_kicker_date / next_kicker_date or any escalation math
// here — this is a percent surface + edit only.
// ============================================================================

import { canViewManagementFee } from './role-gates';
import { useEffect, useState } from 'react';
import { Percent } from 'lucide-react';
import { supabase } from './supabase';
import { rpcErrorMessage } from './rpc-error';
import { useAuth } from './auth';


export const DEFAULT_MGMT_FEE = 0.03; // 3.00% — confirmed default (Ben 2026-07-02; the earlier 2% was a typo). Live data: 169/220 facilities at 3%.

export interface FacilityBillingValues {
  management_fee_pct: number | null;
  tcs_provides_usables: boolean | null;
  usables_detail: Record<string, unknown> | null;
}

// ----------------------------------------------------------------------------
// AFB-0041 — usables tri-state.
//
// tcs_provides_usables is a NULLABLE boolean and all three states are
// meaningful: true = TCS supplies usables, false = it does not, NULL = nobody
// has answered. 157 of 196 Active facilities are NULL. Rendering NULL as an
// unchecked checkbox turned "unknown" into a confident "No", which is how an
// adoption gap became invisible.
// ----------------------------------------------------------------------------
export type UsablesChoice = '' | 'yes' | 'no';

export const USABLES_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not set — nobody has answered' },
  { value: 'yes', label: 'Yes — TCS provides usables' },
  { value: 'no', label: 'No — client provides their own' },
];

/** boolean|null → picker value. NULL stays visibly unanswered. */
export function toUsablesChoice(v: boolean | null | undefined): UsablesChoice {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

/** picker value → boolean|null. '' means "leave unanswered", never false. */
export function fromUsablesChoice(c: UsablesChoice | string): boolean | null {
  if (c === 'yes') return true;
  if (c === 'no') return false;
  return null;
}

/** Read-only label for display surfaces. Never says "No" for an unknown. */
export function usablesLabel(v: boolean | null | undefined): string {
  if (v === true) return 'Yes — TCS provides usables';
  if (v === false) return 'No — client provides their own';
  return 'Not set';
}

/** numeric fraction (0.0200) → editable percent string ("2.00") */
export function pctFromKicker(v: number | null | undefined): string {
  if (v == null || v === 0) return '';
  return String(Math.round(v * 10000) / 100);
}

/** percent string ("2.00") → numeric fraction (0.02), null when unparseable */
export function kickerFromPct(s: string): number | null {
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 10000;
}

async function saveFacilityBilling(
  facilityId: string,
  patch: Partial<FacilityBillingValues>,
): Promise<void> {
  const { error } = await supabase
    .from('facilities')
    .update(patch)
    .eq('facility_id', facilityId);
  if (error) throw error;
}

// ----------------------------------------------------------------------------
// Combined editor card. Used on the facility-detail Financial section and in
// the contracts module (create + detail). Self-contained: edits local state,
// Save persists both fields in one UPDATE, onSaved lets the host refresh.
// ----------------------------------------------------------------------------
export function FacilityBillingCard({
  facilityId,
  values,
  onSaved,
  compact = false,
}: {
  facilityId: string;
  values: FacilityBillingValues;
  onSaved?: () => void;
  compact?: boolean;
}) {
  // Fee visibility gate (Ben ruling 2026-07-07): only finance/superuser see the
  // fee percentage + edit it. Everyone else sees a read-only "has a fee"
  // indicator, no number. Usables stay editable for all.
  const { profile } = useAuth();
  const canSeeFee = canViewManagementFee(profile);
  const hasFee = (values.management_fee_pct ?? 0) > 0;

  const [feeOn, setFeeOn] = useState((values.management_fee_pct ?? 0) > 0);
  const [pct, setPct] = useState(pctFromKicker(values.management_fee_pct));
  // AFB-0041 (2026-07-29) — usables is TRI-STATE, not a checkbox.
  //
  // This was `useState(Boolean(values.tcs_provides_usables))`, which collapsed
  // NULL ("nobody has been asked yet") into an unchecked box that reads as a
  // confident "No". Worse, handleSave() wrote `tcs_provides_usables: usables`
  // unconditionally, so a finance user editing only the management fee on any of
  // the 157 Active facilities with a NULL flag silently converted "unknown" to
  // "No" — manufacturing an answer nobody gave. '' means not set and is never
  // written.
  const [usables, setUsables] = useState<UsablesChoice>(
    toUsablesChoice(values.tcs_provides_usables),
  );
  const [usablesNotes, setUsablesNotes] = useState(
    typeof values.usables_detail?.notes === 'string' ? values.usables_detail.notes : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Re-sync when the host refetches the facility row.
  useEffect(() => {
    setFeeOn((values.management_fee_pct ?? 0) > 0);
    setPct(pctFromKicker(values.management_fee_pct));
    setUsables(toUsablesChoice(values.tcs_provides_usables));
    setUsablesNotes(
      typeof values.usables_detail?.notes === 'string' ? values.usables_detail.notes : '',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, values.management_fee_pct, values.tcs_provides_usables]);

  const feeDirty =
    canSeeFee &&
    (feeOn !== ((values.management_fee_pct ?? 0) > 0) ||
      pct !== pctFromKicker(values.management_fee_pct));
  const usablesDirty =
    usables !== toUsablesChoice(values.tcs_provides_usables) ||
    usablesNotes !==
      (typeof values.usables_detail?.notes === 'string' ? values.usables_detail.notes : '');
  const dirty = feeDirty || usablesDirty;

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const detail: Record<string, unknown> = { ...(values.usables_detail ?? {}) };
      if (usablesNotes.trim()) detail.notes = usablesNotes.trim();
      else delete detail.notes;
      const patch: Partial<FacilityBillingValues> = {
        usables_detail: Object.keys(detail).length > 0 ? detail : null,
      };
      // AFB-0041 — only write the flag when the user actually changed it. A fee
      // edit on a facility whose usables are unknown must leave them unknown;
      // previously this wrote `false` every save and manufactured an answer.
      if (usablesDirty) {
        patch.tcs_provides_usables = fromUsablesChoice(usables);
      }
      // Only finance/superuser may write the fee. Non-finance saves usables only
      // and never touches management_fee_pct.
      if (canSeeFee) {
        patch.management_fee_pct = feeOn ? (kickerFromPct(pct) ?? DEFAULT_MGMT_FEE) : 0;
      }
      await saveFacilityBilling(facilityId, patch);
      setSavedAt(Date.now());
      onSaved?.();
    } catch (err) {
      setError(rpcErrorMessage(err, 'Could not save. Try again, and if it persists tell the COO.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={compact ? '' : 'rounded-xl border border-tcs-border bg-tcs-bg p-5'}>
      {!compact && (
        <div className="flex items-center gap-2">
          <Percent className="h-4 w-4 text-tcs-text-secondary" />
          <h3 className="text-sm font-semibold text-tcs-text">Management fee &amp; usables</h3>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${compact ? '' : 'mt-3'}`}>
        {/* --- Management fee --- */}
        {canSeeFee ? (
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={feeOn}
                onChange={(e) => {
                  setFeeOn(e.target.checked);
                  if (e.target.checked && !pct) setPct(pctFromKicker(DEFAULT_MGMT_FEE));
                }}
                className="h-4 w-4 rounded border-tcs-border"
              />
              <span className="text-sm font-medium text-tcs-text">Apply management fee</span>
            </label>
            {feeOn ? (
              <div className="mt-2 flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  max="99.99"
                  step="0.01"
                  value={pct}
                  onChange={(e) => setPct(e.target.value)}
                  aria-label="Management fee percent"
                  className="block w-24 rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                />
                <span className="text-sm text-tcs-text-secondary">%</span>
                <span className="ml-2 text-xs text-tcs-text-muted">default 3.00%</span>
              </div>
            ) : (
              <p className="mt-2 text-xs text-tcs-text-muted">
                No management fee on this facility (saves as 0.00%).
              </p>
            )}
          </div>
        ) : (
          // Non-finance: read-only indicator only — never the percentage.
          <div>
            <label className="flex items-center gap-2 opacity-90">
              <input
                type="checkbox"
                checked={hasFee}
                disabled
                readOnly
                aria-label="Management fee applies"
                className="h-4 w-4 rounded border-tcs-border"
              />
              <span className="text-sm font-medium text-tcs-text">Management fee</span>
            </label>
            <p className="mt-2 text-xs text-tcs-text-muted">
              {hasFee
                ? 'A management fee applies to this facility. Amount visible to Finance.'
                : 'No management fee recorded on this facility.'}
            </p>
          </div>
        )}

        {/* --- Usables (AFB-0041) — tri-state, never a false "No". --- */}
        <div>
          <label className="block">
            <span className="text-sm font-medium text-tcs-text">Usables account</span>
            <select
              value={usables}
              onChange={(e) => setUsables(e.target.value as UsablesChoice)}
              aria-label="Usables account"
              className="mt-1.5 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            >
              {USABLES_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {usables === '' && (
            <p className="mt-1.5 text-xs text-st-warn-fg">
              Nobody has answered this yet. Ask the account manager rather than assuming.
            </p>
          )}
          {usables === 'yes' && (
            <textarea
              value={usablesNotes}
              onChange={(e) => setUsablesNotes(e.target.value)}
              rows={2}
              placeholder="What TCS provides (liners, paper, chemicals…)"
              aria-label="Usables detail"
              className="mt-2 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          )}
        </div>
      </div>

      {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : canSeeFee ? 'Save fee & usables' : 'Save usables'}
        </button>
        {savedAt && !dirty && !error && (
          <span className="text-xs text-st-ok-fg">Saved.</span>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Self-fetching wrapper — give it a facility_id and it loads the current
// values then renders the editor. Used where the host doesn't already have
// the facility row (contract create form).
// ----------------------------------------------------------------------------
export function FacilityBillingLoader({ facilityId }: { facilityId: string }) {
  const [values, setValues] = useState<FacilityBillingValues | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setValues(null);
    setLoadError(null);
    (async () => {
      const { data, error } = await supabase
        .from('facilities')
        .select('management_fee_pct, tcs_provides_usables, usables_detail')
        .eq('facility_id', facilityId)
        .maybeSingle();
      if (cancelled) return;
      if (error) setLoadError(error.message);
      else if (data) {
        setValues({
          management_fee_pct: data.management_fee_pct,
          tcs_provides_usables: data.tcs_provides_usables,
          usables_detail: (data.usables_detail as Record<string, unknown> | null) ?? null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [facilityId]);

  if (loadError) return <p className="text-xs text-st-danger-fg">{loadError}</p>;
  if (!values) return <p className="text-xs text-tcs-text-muted">Loading…</p>;
  return <FacilityBillingCard facilityId={facilityId} values={values} compact />;
}
